import { getApi, normalizeDeliverable, errorMessage, fetchAccess } from '../services/fexeriaApi';
import { setCompany, assertCompanyHost } from '../services/fexeriaHttp';
import { openFexeriaSocket, closeFexeriaSocket } from '../services/fexeriaSocket';
import { computeRoute, countModule } from '../helpers/progress';
import { TOAST_MS } from '../helpers/constants';

let toastTimer = null;

// El acceso se conserva al cambiar de empresa; los datos se reinician.
const initialAccess = () => ({ loaded: false, denied: false, role: null, canConfigure: false, company: null, userId: null });

const initial = () => ({
  loaded: false,
  loading: false,
  loadError: '',
  companyName: '',
  userName: '',
  deliverables: { fex: [], com: [] },
  journey: null,
  journeyRemote: null,
  toast: '',
});

const findModuleOf = (state, id) => ['fex', 'com'].find((m) => state.deliverables[m].some((x) => x.id === id));

const fexeria_store = {
  namespaced: true,
  state: () => ({ ...initial(), access: initialAccess() }),
  getters: {
    isSuper: (s) => s.access.role === 'admin_sipmovil',
    canConfigure: (s) => s.access.canConfigure,
    companyName: (s) => s.access.company?.name || '',
    hasCompany: (s) => !!s.access.company,
    route: (s) => computeRoute(s.deliverables.com, s.deliverables.fex),
    counts: (s) => ({ fex: countModule(s.deliverables.fex), com: countModule(s.deliverables.com) }),
    latestPublished: (s) => (mod) =>
      s.deliverables[mod]
        .filter((x) => x.visible)
        .sort((a, b) => new Date(b.published_at || 0) - new Date(a.published_at || 0))[0] || null,
  },
  mutations: {
    RESET(state) {
      Object.assign(state, initial());
    },
    SET_ACCESS(state, access) {
      state.access = { loaded: true, ...access };
    },
    SET_LOADING(state, v) {
      state.loading = v;
    },
    SET_LOAD_ERROR(state, msg) {
      state.loadError = msg;
    },
    SET_LOADED(state, { companyName, userName }) {
      state.loaded = true;
      state.companyName = companyName;
      state.userName = userName;
    },
    SET_LIST(state, { module, list }) {
      state.deliverables[module] = list;
    },
    UPSERT(state, item) {
      const list = state.deliverables[item.module];
      const i = list.findIndex((x) => x.id === item.id);
      if (i === -1) list.push(item);
      else list.splice(i, 1, item);
    },
    REMOVE(state, id) {
      const mod = findModuleOf(state, id);
      if (mod) state.deliverables[mod] = state.deliverables[mod].filter((x) => x.id !== id);
    },
    SET_JOURNEY(state, journey) {
      state.journey = journey;
    },
    SET_JOURNEY_REMOTE(state, journey) {
      state.journeyRemote = journey;
    },
    SET_TOAST(state, msg) {
      state.toast = msg;
    },
  },
  actions: {
    api() {
      return getApi();
    },

    async loadAccess({ commit, dispatch, state, rootState }) {
      let access;
      try {
        access = await fetchAccess(rootState.auth_store.userInfo);
      } catch (e) {
        if (e?.code === 'ERR_CANCELED') return state.access;
        throw e;
      }
      commit('SET_ACCESS', access);
      if (access.error) dispatch('toast', access.error);
      return access;
    },

    // Admin SipMóvil: la empresa y su host se guardan solo en esta pestaña (sin change_company).
    async selectCompany({ commit, dispatch }, company) {
      assertCompanyHost(company);
      closeFexeriaSocket();
      setCompany(company);
      commit('RESET');
      const access = await dispatch('loadAccess');
      if (!access.company) setCompany(null);
      return access;
    },

    forgetCompany({ commit, state }) {
      closeFexeriaSocket();
      setCompany(null);
      commit('RESET');
      commit('SET_ACCESS', { ...state.access, company: null });
    },

    // Los eventos no traen contenido: se vuelve a pedir por la API. Se ignoran los del propio usuario.
    openSocket({ dispatch, state }) {
      openFexeriaSocket((evt) => {
        if (evt.actor_id != null && evt.actor_id === state.access.userId) return;
        if (evt.event === 'deliverables.changed') {
          dispatch('refresh', evt.module === 'fex' || evt.module === 'com' ? [evt.module] : undefined);
        } else if (evt.event === 'journey.changed') {
          dispatch('onJourneyChanged');
        }
      }, () => {
        // Tras reconectar pudieron perderse eventos.
        dispatch('refresh');
        dispatch('onJourneyChanged');
      });
    },
    // El panel de edición decide si aplica el cambio (no pisa cambios sin guardar).
    async onJourneyChanged({ commit, dispatch, state }) {
      if (!state.journey) return;
      try {
        const api = await dispatch('api');
        commit('SET_JOURNEY_REMOTE', await api.getJourney());
      } catch (e) {
        // se verá al volver a abrir el viaje
      }
    },
    closeSocket() {
      closeFexeriaSocket();
    },

    toast({ commit }, msg) {
      if (!msg) return;
      commit('SET_TOAST', msg);
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => commit('SET_TOAST', ''), TOAST_MS);
    },

    async loadAll({ commit, dispatch, state, rootState }) {
      commit('SET_LOADING', true);
      commit('SET_LOAD_ERROR', '');
      try {
        const api = await dispatch('api');
        const [fex, com] = await Promise.all([api.listDeliverables('fex'), api.listDeliverables('com')]);
        const userInfo = rootState.auth_store.userInfo;
        commit('SET_LIST', { module: 'fex', list: fex.map(normalizeDeliverable) });
        commit('SET_LIST', { module: 'com', list: com.map(normalizeDeliverable) });
        commit('SET_LOADED', {
          companyName: state.access.company?.name || '',
          userName: userInfo?.ShortName || userInfo?.Username || '',
        });
      } catch (e) {
        const msg = errorMessage(e, 'No se pudo cargar la información de Fexería.');
        commit('SET_LOAD_ERROR', msg || '');
        dispatch('toast', msg);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async refresh({ commit, dispatch }, modules = ['fex', 'com']) {
      try {
        const api = await dispatch('api');
        await Promise.all(
          modules.map(async (module) => {
            const list = await api.listDeliverables(module);
            commit('SET_LIST', { module, list: list.map(normalizeDeliverable) });
          }),
        );
      } catch (e) {
        // se reintenta en la próxima pasada
      }
    },

    async createDeliverable({ commit, dispatch }, payload) {
      const api = await dispatch('api');
      const { type, ...rest } = payload;
      const item = normalizeDeliverable(await api.createDeliverable({ ...rest, kind: type === 'link' ? 'link' : 'archivo' }));
      commit('UPSERT', item);
      return item;
    },
    async updateDeliverable({ commit, dispatch }, { id, patch }) {
      const api = await dispatch('api');
      const item = normalizeDeliverable(await api.updateDeliverable(id, patch));
      commit('UPSERT', item);
      return item;
    },
    async deleteDeliverable({ commit, dispatch, state }, id) {
      const mod = ['fex', 'com'].find((m) => state.deliverables[m].some((x) => x.id === id));
      const api = await dispatch('api');
      await api.deleteDeliverable(id);
      commit('REMOVE', id);
      // El back recorre las posiciones: se vuelve a pedir la lista.
      if (mod) commit('SET_LIST', { module: mod, list: (await api.listDeliverables(mod)).map(normalizeDeliverable) });
    },
    async moveDeliverable({ state, commit, dispatch }, { module, id, dir }) {
      const list = [...state.deliverables[module]];
      const i = list.findIndex((x) => x.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= list.length) return;
      [list[i], list[j]] = [list[j], list[i]];
      commit('SET_LIST', { module, list: list.map((x, k) => ({ ...x, order: k + 1 })) });
      try {
        const api = await dispatch('api');
        // Va la lista completa del módulo; la respuesta es la fuente de verdad.
        const res = await api.reorder(module, list.map((x) => x.id));
        if (Array.isArray(res)) commit('SET_LIST', { module, list: res.map(normalizeDeliverable) });
      } catch (e) {
        const api = await dispatch('api');
        commit('SET_LIST', { module, list: (await api.listDeliverables(module)).map(normalizeDeliverable) });
        throw e;
      }
    },
    async setContent({ commit, dispatch }, { id, file, url }) {
      const api = await dispatch('api');
      const item = normalizeDeliverable(await api.setContent(id, { file, url }));
      commit('UPSERT', item);
      return item;
    },
    async removeContent({ commit, dispatch }, id) {
      const api = await dispatch('api');
      const item = normalizeDeliverable(await api.removeContent(id));
      commit('UPSERT', item);
      return item;
    },
    async setPublished({ commit, dispatch, state }, { id, visible }) {
      const api = await dispatch('api');
      const cur = state.deliverables.fex.concat(state.deliverables.com).find((x) => x.id === id);
      const current = cur && {
        id: cur.id, module: cur.module, kind: cur.kind, name: cur.name, description: cur.description, stage: cur.stage,
        position: cur.order, has_content: cur.hasContent, is_published: cur.visible, published_at: cur.published_at,
        file_name: cur.content?.file_name, file_size: cur.content?.size, url: cur.content?.url,
      };
      const item = normalizeDeliverable(await api.setPublished(id, visible, current));
      commit('UPSERT', item);
      return item;
    },
    async download({ dispatch }, { id, inline = false }) {
      const api = await dispatch('api');
      return api.download(id, { inline });
    },

    async loadJourney({ commit, dispatch }) {
      const api = await dispatch('api');
      const journey = await api.getJourney();
      commit('SET_JOURNEY', journey);
      return journey;
    },
    async saveJourney({ commit, dispatch }, journey) {
      const api = await dispatch('api');
      const saved = await api.saveJourney(journey);
      commit('SET_JOURNEY', saved || journey);
      return saved;
    },
    async importJourney({ commit, dispatch }, raw) {
      const api = await dispatch('api');
      const saved = await api.importJourney(raw);
      commit('SET_JOURNEY', saved);
      return saved;
    },
    async exportJourney({ dispatch }) {
      const api = await dispatch('api');
      return api.exportJourney();
    },
    async restoreJourney({ commit, dispatch }) {
      const api = await dispatch('api');
      const saved = await api.restoreJourney();
      commit('SET_JOURNEY', saved);
      return saved;
    },
  },
};

export default fexeria_store;
